/**
 * Cómo se escriben los "min de charla" — UNA sola regla para toda la app.
 *
 * El número que se muestra es el **redondeo normal** de segundos a minutos, y
 * `"<1"` aparece **sólo cuando ese redondeo daría 0**. Nada de un minuto
 * inventado (había un piso "habló pero da 0 → mostrá 1" que tapaba que la
 * métrica vieja no medía la charla escrita) y nada de un "0 min" que se lee
 * como bug en una charla que existió.
 *
 * El corte en 30 s no es arbitrario: es dónde cae el redondeo. Así el debrief,
 * la fila del historial, el detalle de la charla, la Home, el Camino, Totales
 * y el mail semanal dicen todos lo mismo de la misma charla. Antes convivían
 * dos reglas —el historial cortaba en 60 s y el resto redondeaba— y la misma
 * charla de 45 s decía "1m" en una pantalla y "<1m" en la de al lado.
 *
 * Ojo con el caso CERO: `0` no es lo mismo que "menos de un minuto". Una charla
 * enteramente escrita tiene 0 segundos hablados y eso es un dato real, no un
 * redondeo. Por eso {@link formatChatMinutes} lo distingue explícitamente y los
 * callers eligen qué decir.
 */

/** Segundos a partir de los cuales el redondeo da ≥ 1 minuto. */
export const SUB_MINUTE_THRESHOLD_SECONDS = 30;

export type ChatMinutes =
  /** Exactamente 0: no hubo nada de esto (ej. una charla sin una palabra hablada). */
  | { kind: 'zero'; text: '0' }
  /** Hubo, pero redondea a 0. */
  | { kind: 'sub-minute'; text: '<1' }
  /** Un número de minutos ≥ 1. */
  | { kind: 'minutes'; text: string; value: number };

/**
 * Traduce segundos a lo que se muestra. `null` = todavía no se puede calcular
 * (el backend no persistió `ended_at`) y lo maneja el caller: acá no inventamos
 * un valor provisional.
 */
export function formatChatMinutes(seconds: number): ChatMinutes {
  if (seconds <= 0) return { kind: 'zero', text: '0' };
  if (seconds < SUB_MINUTE_THRESHOLD_SECONDS) return { kind: 'sub-minute', text: '<1' };
  const value = Math.round(seconds / 60);
  return { kind: 'minutes', text: String(value), value };
}

/** Variante compacta con la unidad pegada, para las micro-stats del historial. */
export function formatChatMinutesShort(seconds: number): string {
  return `${formatChatMinutes(seconds).text}m`;
}
